import React, { useState } from "react";
import { Brain, Maximize2, CalendarCheck } from "lucide-react";

const card = "rounded-2xl border border-[#00ff90]/20 bg-[#080808] shadow-[0_0_24px_#00ff9010]";

export default function LeaveWidget({ data = {}, period, loading, onAskKira, onExpand, expanded }) {
  const [tab, setTab] = useState("overview");

  const types     = data.by_type      || [];
  const upcoming  = data.upcoming_peaks|| [];
  const byDept    = data.by_department || [];

  const COLORS = ["#00ff90","#60a5fa","#fbbf24","#e879f9","#fb923c","#34d399"];

  return (
    <div className={`${card} flex flex-col`}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
        <div className="flex items-center gap-2">
          <CalendarCheck size={14} className="text-[#fbbf24]/60" />
          <span className="text-sm font-semibold text-white/80">Leave</span>
          {data.pending_approvals > 0 && (
            <span className="text-xs bg-yellow-400/20 text-yellow-400 border border-yellow-400/30 px-2 py-0.5 rounded-full ml-1">
              {data.pending_approvals} pending
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => onAskKira(data, "Analyse leave patterns: utilisation rates, pending approvals, upcoming peaks. What should I be aware of and what actions should I take?")}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-[#fbbf24]/20 bg-[#fbbf24]/5 text-[#fbbf24]/60 hover:text-[#fbbf24] transition-colors">
            <Brain size={11} /> KIRA
          </button>
          {onExpand && <button onClick={onExpand} className="text-white/20 hover:text-white transition-colors"><Maximize2 size={13} /></button>}
        </div>
      </div>

      <div className="flex gap-1 px-4 pt-3">
        {["overview","by type","upcoming","departments"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`text-xs px-3 py-1.5 rounded-lg transition-colors capitalize ${
              tab === t ? "bg-[#fbbf24]/10 text-[#fbbf24] border border-[#fbbf24]/20" : "text-white/30 hover:text-white/60"
            }`}>{t}</button>
        ))}
      </div>

      <div className="p-5 flex-1 space-y-4">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-8 rounded-lg bg-white/5 animate-pulse" />)}</div>
        ) : (
          <>
          {tab === "overview" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "On Leave Today",    value: data.on_leave_today ?? "—",     color: "#fbbf24" },
                  { label: "Pending Approvals", value: data.pending_approvals ?? "—",  color: data.pending_approvals > 5 ? "#f87171" : "#fbbf24" },
                  { label: "Utilisation Rate",  value: data.utilisation_rate ? `${data.utilisation_rate}%` : "—", color: "#00ff90" },
                  { label: "Avg Days Taken",    value: data.avg_days_taken ? `${data.avg_days_taken}d` : "—", color: "#60a5fa" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="rounded-xl border border-white/5 bg-white/3 p-3">
                    <p className="text-xs text-white/30 mb-1">{label}</p>
                    <p className="text-xl font-bold font-mono" style={{ color }}>{value}</p>
                  </div>
                ))}
              </div>

              {/* Pending approvals list */}
              {data.pending_list?.length > 0 && (
                <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/3 p-4 space-y-2">
                  <p className="text-xs text-yellow-400/70 uppercase tracking-wider mb-2">Awaiting Approval</p>
                  {data.pending_list.slice(0, 4).map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-white/60">{r.name}</span>
                      <div className="flex items-center gap-2 text-white/30">
                        <span>{r.type}</span>
                        <span>·</span>
                        <span>{r.days}d</span>
                        <span>·</span>
                        <span>{r.from}</span>
                      </div>
                    </div>
                  ))}
                  {data.pending_list.length > 4 && (
                    <p className="text-xs text-yellow-400/40">+{data.pending_list.length - 4} more</p>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === "by type" && (
            <div className="space-y-3">
              {types.length === 0 ? (
                <p className="text-white/20 text-sm italic text-center py-6">No leave type data.</p>
              ) : (
                <>
                  {/* Donut-style breakdown */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {types.map((t, i) => (
                      <div key={i} className="rounded-lg border border-white/5 bg-white/3 px-3 py-2 flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="text-xs text-white/50">{t.type}</span>
                        <span className="text-xs font-mono font-bold ml-1" style={{ color: COLORS[i % COLORS.length] }}>{t.days_taken}d</span>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {types.map((t, i) => (
                      <div key={i} className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-white/50">{t.type}</span>
                          <span className="text-white/30">{t.days_taken}d taken / {t.days_available}d available</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/5">
                          <div className="h-full rounded-full"
                            style={{ width: `${t.days_available > 0 ? Math.round((t.days_taken/t.days_available)*100) : 0}%`, background: COLORS[i % COLORS.length], opacity: 0.7 }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "upcoming" && (
            <div className="space-y-3">
              <p className="text-xs text-white/30">Leave density over next 30 days</p>
              {upcoming.length === 0 ? (
                <p className="text-white/20 text-sm italic text-center py-6">No upcoming leave data.</p>
              ) : (
                <div className="grid grid-cols-7 gap-1">
                  {upcoming.map((d, i) => {
                    const intensity = Math.min(d.count / 10, 1);
                    const bg = d.count === 0 ? "bg-white/3" : "";
                    return (
                      <div key={i} title={`${d.date}: ${d.count} on leave`}
                        className={`aspect-square rounded-sm flex items-center justify-center text-[9px] font-mono cursor-default ${bg}`}
                        style={{ background: d.count > 0 ? `rgba(251,191,36,${0.1 + intensity * 0.6})` : undefined }}>
                        {d.count > 0 ? d.count : ""}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex items-center gap-3 text-xs text-white/20 mt-2">
                <span>Lighter = fewer on leave</span>
                <div className="flex gap-0.5">
                  {[0.1,0.3,0.5,0.7,0.9].map((o,i) => (
                    <div key={i} className="w-3 h-3 rounded-sm" style={{ background: `rgba(251,191,36,${o})` }} />
                  ))}
                </div>
                <span>Darker = more</span>
              </div>
            </div>
          )}

          {tab === "departments" && (
            <div className="space-y-2">
              {byDept.length === 0 ? (
                <p className="text-white/20 text-sm italic text-center py-6">No data.</p>
              ) : byDept.map((d, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl border border-white/5 bg-white/3 px-4 py-3">
                  <span className="text-sm text-white/60">{d.dept}</span>
                  <div className="flex gap-4 text-xs font-mono text-white/40">
                    <span>{d.on_leave_today} today</span>
                    <span className="text-[#fbbf24]/60">{d.pending} pending</span>
                    <span className="text-[#00ff90]/60">{d.utilisation_rate}% util</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}